import React, { createElement, CSSProperties, useMemo } from 'react'

import { Skeleton } from 'antd'
import { useParams } from 'react-router-dom'
import useSWR from 'swr'

import { getClusterInfo, getReport } from '~/api'
import { Header } from '~/componentv2/Header'

import { ErrorMsg } from '../../shared/components/Error'
import { MetricStats, ReportBasic } from '../../shared/types'
import { usePrevPath } from '../../shared/utils/prev_path'
import { tables, Table } from '../schema'
import { ComponentData, componentMap } from '../table_component'
import { SchemaColumn } from '../types'
import { Desc } from './Desc'
import { SnapInfo } from './SnapInfo'
import { Topology } from './Topology'

export type MetricMap = { [name: string]: MetricStats }

const WorkloadReportPage: React.FC = () => {
  const { orgId, cid, rid } = useParams<'orgId' | 'cid' | 'rid'>()
  const { data: clusterInfoRes, error: clusterError } = useSWR(
    `/orgs/${orgId}/clusters/${cid}`,
    () => getClusterInfo(orgId!, cid!)
  )
  const isClusterLoading = !clusterInfoRes && !clusterError
  const { data: reportRes, error: reportError } = useSWR(
    `/reports/workload/${rid}`,
    () => getReport(orgId!, cid!, rid!)
  )
  const isReportLoading = !reportRes && !reportError
  const isLoading = isClusterLoading || isReportLoading
  const prevPath = usePrevPath()
  const metrics = useMemo<MetricMap>(() => {
    const ms = (reportRes?.data?.data as any)?.metricStats as MetricStats[]
    if (!ms) {
      return {}
    }
    return ms.reduce((prev, cur) => {
      prev[cur.name] = cur
      return prev
    }, {} as MetricMap)
  }, [reportRes])

  return (
    <>
      <Header />
      <div className='m-auto max-w-[1440px]'>
        <h1 className='p-4 mt-4'>TiDB Cluster System Check Report</h1>
        <Desc />
        <div className='m-4 p-4 bg-white'>
          {isLoading ? (
            <Skeleton active />
          ) : !!reportError ? (
            <ErrorMsg
              title={`${reportError.toString()}: ${
                reportError?.response?.data?.message
              }`}
              backTo={prevPath}
            />
          ) : (
            <>
              <SnapInfo
                cluster={clusterInfoRes?.data}
                report={reportRes?.data! as ReportBasic}
                metrics={metrics}
              />
              <Topology metrics={metrics} />
              <IndexTable metrics={metrics} defaultLevels={[2]} />
            </>
          )}
        </div>
      </div>
    </>
  )
}

const IndexTable: React.FC<{
  metrics: MetricMap
  defaultLevels?: number[]
}> = ({ metrics, defaultLevels = [0] }) => {
  const indexes = useMemo<string[]>(() => {
    let indexIndicator: number[] = [...defaultLevels]
    const idxs: string[] = []

    for (let i = 0; i < tables.length; i++) {
      const cur = tables[i]
      const curLevel = cur.schema.level || 0
      const curIndicatorValue = indexIndicator[curLevel] || 0

      indexIndicator[curLevel] = curIndicatorValue + 1
      idxs.push(indexIndicator.join('.'))

      const next = tables[i + 1]
      if (!next) {
        break
      }

      const nextLevel = next.schema.level || 0
      if (nextLevel >= curLevel) {
        continue
      }
      indexIndicator = indexIndicator.slice(0, nextLevel + 1)
    }

    return idxs
  }, [defaultLevels])

  return (
    <>
      {tables.map((table, index) => (
        <React.Fragment key={index}>
          {createElement(
            `h${(table.schema.level || 0) + 1}`,
            {
              className: 'mt-6'
            },
            // 1. Title
            // 1.1 Subtitle
            `${indexes[index]}${!table.schema.level ? '.' : ''} ${
              table.schema.title
            }`
          )}
          {!!table.schema.subTitle && (
            <p dangerouslySetInnerHTML={{ __html: table.schema.subTitle }}></p>
          )}
          {!!table.schema.columns && (
            <MetricTable metrics={metrics} table={table} />
          )}
        </React.Fragment>
      ))}
    </>
  )
}

const MetricTable: React.FC<{
  metrics: MetricMap
  table: Table
}> = ({ metrics, table }) => {
  const { schema, data } = table
  const getStyles = (col: SchemaColumn): CSSProperties =>
    col.width ? { minWidth: col.width } : {}
  return (
    <table className='border-collapse'>
      <thead>
        <tr>
          {schema.columns!.map((col) => (
            <th
              className='text-left bg-[#fafafa] px-2 py-1 border-r border-solid border-[#f0f0f0]'
              key={col.id}
              style={getStyles(col)}
            >
              {col.name}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data?.map((row, rowIndex) => {
          return (
            <tr key={rowIndex}>
              {schema.columns!.map((col) => {
                const d = row[col.id]
                const Comp = (d?.__component__ ||
                  componentMap[col.component]) as React.FC<ComponentData>
                return (
                  <td
                    key={col.id}
                    className='px-2 py-1 border-r border-solid border-[#f0f0f0]'
                    style={{ ...getStyles(col), overflowWrap: 'anywhere' }}
                  >
                    <Comp
                      rowIndex={rowIndex}
                      metricStats={metrics}
                      column={col}
                      value={d}
                    />
                  </td>
                )
              })}
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

export default WorkloadReportPage
